import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usersAPI } from '../../api/api';
import CustomDropdown from '../../components/CustomDropdown';
import './Users.css';

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [newUser, setNewUser] = useState({
    username: '',
    position: '',
    department: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'employee'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editPassword, setEditPassword] = useState({
    password: '',
    confirmPassword: ''
  });
  const roleOptions = [
    { value: 'employee', label: 'Сотрудник'},
    { value: 'admin', label: 'Администратор'}
  ];

  const statusOptions = [
    { value: 'active', label: 'Активен'},
    { value: 'inactive', label: 'Неактивен'}
  ];
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showEditConfirmPassword, setShowEditConfirmPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAll();
      setUsers(response.users || []);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      alert('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const validatePassword = (password, confirmPassword) => {
    const errors = {};
    
    if (password.length < 6) {
      errors.password = 'Пароль должен содержать минимум 6 символов';
    }
    
    if (password !== confirmPassword) {
      errors.confirmPassword = 'Пароли не совпадают';
    }
    
    return errors;
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    // Если номер полностью удален, возвращаем пустую строку
    if (numbers.length === 0) return '';
    
    // Если начинается с 7, убираем первую 7 (оставляем только +7)
    const cleanNumbers = numbers.startsWith('7') ? numbers.slice(1) : numbers;
    
    if (cleanNumbers.length === 0) return '+7';
    if (cleanNumbers.length <= 3) return `+7 (${cleanNumbers}`;
    if (cleanNumbers.length <= 6) return `+7 (${cleanNumbers.slice(0, 3)}) ${cleanNumbers.slice(3)}`;
    if (cleanNumbers.length <= 8) return `+7 (${cleanNumbers.slice(0, 3)}) ${cleanNumbers.slice(3, 6)}-${cleanNumbers.slice(6)}`;
    return `+7 (${cleanNumbers.slice(0, 3)}) ${cleanNumbers.slice(3, 6)}-${cleanNumbers.slice(6, 8)}-${cleanNumbers.slice(8, 10)}`;
  };

  const handlePhoneChange = (value, isNewUser = true) => {
    const formattedPhone = formatPhone(value);
    if (isNewUser) {
      setNewUser(prev => ({ ...prev, phone: formattedPhone }));
    } else {
      setSelectedUser(prev => ({ ...prev, phone: formattedPhone }));
    }
  };

  const handlePhoneKeyDown = (e, isNewUser = true) => {
    // Если нажата Backspace и в поле только "+7", очищаем поле полностью
    if (e.key === 'Backspace') {
      const currentPhone = isNewUser ? newUser.phone : selectedUser.phone;
      if (currentPhone === '+7') {
        e.preventDefault();
        if (isNewUser) {
          setNewUser(prev => ({ ...prev, phone: '' }));
        } else {
          setSelectedUser(prev => ({ ...prev, phone: '' }));
        }
      }
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    const validationErrors = {};
    
    const passwordErrors = validatePassword(newUser.password, newUser.confirmPassword);
    Object.assign(validationErrors, passwordErrors);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setErrors({});
    
    try {
      const { confirmPassword, ...userData } = newUser;
      await usersAPI.create(userData);
      alert('Пользователь успешно добавлен');
      setShowAddModal(false);
      setNewUser({
        username: '',
        position: '',
        department: '',
        phone: '',
        password: '',
        confirmPassword: '',
        role: 'employee'
      });
      await loadUsers();
    } catch (error) {
      console.error('Ошибка добавления пользователя:', error);
      alert(`Ошибка добавления пользователя: ${error.message}`);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    
    const validationErrors = {};
    
    if (changePassword) {
      const passwordErrors = validatePassword(editPassword.password, editPassword.confirmPassword);
      Object.assign(validationErrors, passwordErrors);
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setErrors({});
    
    try {
      const { id, ...userData } = selectedUser;
      
      if (changePassword && editPassword.password) {
        userData.password = editPassword.password;
      }
      
      await usersAPI.update(id, userData);
      alert('Пользователь успешно обновлен');
      setShowEditModal(false);
      setSelectedUser(null);
      setEditPassword({
        password: '',
        confirmPassword: ''
      });
      setChangePassword(false);
      await loadUsers();
    } catch (error) {
      console.error('Ошибка обновления пользователя:', error);
      alert(`Ошибка обновления пользователя: ${error.message}`);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя ${username}?`)) {
      return;
    }

    try {
      await usersAPI.delete(userId);
      alert('Пользователь успешно удален');
      await loadUsers();
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      alert(`Ошибка удаления пользователя: ${error.message}`);
    }
  };

  const openEditModal = (user) => {
    setSelectedUser({ ...user });
    setEditPassword({
      password: '',
      confirmPassword: ''
    });
    setChangePassword(false);
    setShowEditModal(true);
    setErrors({});
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewUser({
      username: '',
      position: '',
      department: '',
      phone: '',
      password: '',
      confirmPassword: '',
      role: 'employee'
    });
    setErrors({});
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const canManageUsers = user?.role === 'admin';

  const formatDate = (dateString) => {
  if (!dateString) return 'Никогда';
  
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return 'Неверная дата';
    }
    
    // Прибавляем 3 часа для московского времени (UTC+3)
    const moscowTime = new Date(date.getTime() + (3 * 60 * 60 * 1000));
    
    return moscowTime.toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Ошибка форматирования даты:', error);
    return 'Ошибка даты';
  }
};

  if (loading) {
    return (
      <div className="users-page">
        <div className="loading">Загрузка пользователей...</div>
      </div>
    );
  }

  return (
    <div className="users-page">
      

      <div className="users-grid">
        {/* Карточка для добавления нового пользователя */}
        {canManageUsers && (
          <div className="user-card add-user-card" onClick={() => setShowAddModal(true)}>
            <div className="add-user-content">
              <div className="add-user-icon">
                <i className="fas fa-plus"></i>
              </div>
              <h3>Добавить сотрудника</h3>
              <p>Нажмите чтобы добавить нового сотрудника</p>
            </div>
          </div>
        )}

        {/* Карточки существующих пользователей */}
        {users.map(userItem => (
          <div key={userItem.id} className="user-card">
            <div className="user-info">
              <h3>{userItem.username}</h3>
              <p className="user-position">{userItem.position}</p>
              {userItem.department && (
                <p className="user-department">Отдел: {userItem.department}</p>
              )}
              {userItem.phone && (
                <p className="user-phone">Телефон: {userItem.phone}</p>
              )}
              <div className="user-meta">
                <span className={`role-badge ${userItem.role}`}>
                  {userItem.role === 'admin' ? 'Администратор' : 
                   userItem.role === 'manager' ? 'Менеджер' : 'Сотрудник'}
                </span>
                <span className={`status-badge ${userItem.status || 'active'}`}>
                  {userItem.status === 'active' ? 'Активен' : 'Неактивен'}
                </span>
              </div>
              <div className="user-last-login">
                Последний вход: {formatDate(userItem.last_login)}
              </div>
            </div>
            {canManageUsers && (
              <div className="user-actions">
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={() => openEditModal(userItem)}
                  title="Редактировать"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button 
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteUser(userItem.id, userItem.username)}
                  title="Удалить"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Модальное окно добавления сотрудника */}
      {showAddModal && (
        <div className="modal-overlay">
    <div className="modal">
      <div className="modal-header">
        <h3>Добавить сотрудника</h3>
        <button className="close" onClick={closeAddModal}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      <form onSubmit={handleAddUser}>
        <div className="modal-content">
                <div className="form-group">
                  <label>Имя пользователя *</label>
                  <input
                    type="text"
                    value={newUser.username}
                    onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                    placeholder="ivanov"
                    className={errors.username ? 'error' : ''}
                    required
                  />
                  {errors.username && <span className="error-text">{errors.username}</span>}
                </div>
                
                <div className="form-group">
                  <label>Пароль *</label>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      placeholder="••••••••"
                      className={errors.password ? 'error' : ''}
                      required
                    />
                    <button 
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  {errors.password && <span className="error-text">{errors.password}</span>}
                </div>
                
                <div className="form-group">
                  <label>Подтвердить пароль *</label>
                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={newUser.confirmPassword}
                      onChange={(e) => setNewUser({...newUser, confirmPassword: e.target.value})}
                      placeholder="••••••••"
                      className={errors.confirmPassword ? 'error' : ''}
                      required
                    />
                    <button 
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                </div>
                
                <div className="form-group">
                  <label>Должность</label>
                  <input
                    type="text"
                    value={newUser.position}
                    onChange={(e) => setNewUser({...newUser, position: e.target.value})}
                    placeholder="Оператор"
                  />
                </div>
                
                <div className="form-group">
                  <label>Отдел</label>
                  <input
                    type="text"
                    value={newUser.department}
                    onChange={(e) => setNewUser({...newUser, department: e.target.value})}
                    placeholder="Производство"
                  />
                </div>
                
                <div className="form-group">
                  <label>Телефон *</label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => handlePhoneChange(e.target.value, true)}
                    onKeyDown={(e) => handlePhoneKeyDown(e, true)}
                    placeholder="+7 (999) 123-45-67"
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
                
                <div className="form-group">
                  <label>Роль</label>
                  <CustomDropdown
              options={roleOptions}
              value={newUser.role}
              onChange={(value) => setNewUser({...newUser, role: value})}
              placeholder="Выберите роль..."
            />
          </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="add-button">
                  Добавить сотрудника
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования сотрудника */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay">
    <div className="modal">
      <div className="modal-header">
        <h3>Редактировать сотрудника</h3>
        <button className="close" onClick={() => setShowEditModal(false)}>
          <i className="fas fa-times"></i>
        </button>
      </div>
      <form onSubmit={handleEditUser}>
        <div className="modal-content">
                <div className="form-group">
                  <label>Имя пользователя *</label>
                  <input
                    type="text"
                    value={selectedUser.username}
                    onChange={(e) => setSelectedUser({...selectedUser, username: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={changePassword}
                      onChange={(e) => setChangePassword(e.target.checked)}
                    />
                    <span className="checkbox-text">Сменить пароль</span>
                  </label>
                </div>

                {changePassword && (
                  <>
                    <div className="form-group">
                      <label>Новый пароль *</label>
                      <div className="password-input-container">
                        <input
                          type={showEditPassword ? "text" : "password"}
                          value={editPassword.password}
                          onChange={(e) => setEditPassword({...editPassword, password: e.target.value})}
                          placeholder="••••••••"
                          className={errors.password ? 'error' : ''}
                          required
                        />
                        <button 
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowEditPassword(!showEditPassword)}
                        >
                          <i className={`fas ${showEditPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                      {errors.password && <span className="error-text">{errors.password}</span>}
                    </div>
                    
                    <div className="form-group">
                      <label>Подтвердить новый пароль *</label>
                      <div className="password-input-container">
                        <input
                          type={showEditConfirmPassword ? "text" : "password"}
                          value={editPassword.confirmPassword}
                          onChange={(e) => setEditPassword({...editPassword, confirmPassword: e.target.value})}
                          placeholder="••••••••"
                          className={errors.confirmPassword ? 'error' : ''}
                          required
                        />
                        <button 
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowEditConfirmPassword(!showEditConfirmPassword)}
                        >
                          <i className={`fas ${showEditConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                      {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                    </div>
                  </>
                )}
                
                <div className="form-group">
                  <label>Должность</label>
                  <input
                    type="text"
                    value={selectedUser.position || ''}
                    onChange={(e) => setSelectedUser({...selectedUser, position: e.target.value})}
                    placeholder="Оператор"
                  />
                </div>
                
                <div className="form-group">
                  <label>Отдел</label>
                  <input
                    type="text"
                    value={selectedUser.department || ''}
                    onChange={(e) => setSelectedUser({...selectedUser, department: e.target.value})}
                    placeholder="Производство"
                  />
                </div>
                
                <div className="form-group">
                  <label>Телефон *</label>
                  <input
                    type="tel"
                    value={selectedUser.phone || ''}
                    onChange={(e) => handlePhoneChange(e.target.value, false)}
                    onKeyDown={(e) => handlePhoneKeyDown(e, false)}
                    placeholder="+7 (999) 123-45-67"
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
                
                 <div className="form-group">
            <label>Роль</label>
            <CustomDropdown
              options={roleOptions}
              value={selectedUser.role}
              onChange={(value) => setSelectedUser({...selectedUser, role: value})}
              placeholder="Выберите роль..."
            />
          </div>
          
          <div className="form-group">
            <label>Статус</label>
            <CustomDropdown
              options={statusOptions}
              value={selectedUser.status || 'active'}
              onChange={(value) => setSelectedUser({...selectedUser, status: value})}
            />
          </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="add-button">
                  Сохранить изменения
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
